import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { axiosObj } from "../../../../config/apiClients";
import { MarketUpdateState } from "../../../types";
import { MarketUpdate, MarketUpdateStatusTab } from "../../../../utils/types";

const initialState: MarketUpdateState = {
  isLoading: false,
  isMutating: false,
  marketUpdates: [],
  pagination: {
    totalItems: 0,
    currentPage: 1,
    totalPages: 1,
    hasNextPage: false,
  },
};

export const getAllMarketUpdates = createAsyncThunk(
  "/market-update/getAll",
  async (
    {
      paramPage = 1,
      paramLimit = 10,
      status,
      search = "",
    }: { paramPage?: number; paramLimit?: number; status: MarketUpdateStatusTab; search?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await axiosObj.get("/market-update/getAll", {
        params: { paramPage, paramLimit, status, search },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: "Failed to fetch market updates" });
    }
  },
);

export const createMarketUpdate = createAsyncThunk(
  "/market-update/create",
  async (payload: MarketUpdate, { rejectWithValue }) => {
    try {
      const response = await axiosObj.post("/market-update/create", payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: "Failed to create market update" });
    }
  },
);

export const updateMarketUpdate = createAsyncThunk(
  "/market-update/update",
  async (payload: MarketUpdate & { _id: string }, { rejectWithValue }) => {
    try {
      const { _id, ...rest } = payload;
      const response = await axiosObj.patch("/market-update/update", { id: _id, ...rest });
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: "Failed to update market update" });
    }
  },
);

export const deleteMarketUpdate = createAsyncThunk(
  "/market-update/delete",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await axiosObj.delete("/market-update/delete", { data: { id } });
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: "Failed to delete market update" });
    }
  },
);

const marketUpdateSlice = createSlice({
  name: "marketUpdate",
  initialState,
  reducers: {
    removeMarketUpdate: (state, action) => {
      state.marketUpdates = state.marketUpdates.filter(
        (item) => item._id !== action.payload,
      );
      state.pagination.totalItems = Math.max(0, state.pagination.totalItems - 1);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllMarketUpdates.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAllMarketUpdates.fulfilled, (state, action) => {
        state.isLoading = false;
        const payload = action.payload?.payload;
        state.marketUpdates = payload?.marketUpdates ?? [];
        state.pagination = {
          totalItems: payload?.totalItems ?? 0,
          currentPage: payload?.page ?? 1,
          totalPages: payload?.totalPages ?? 1,
          hasNextPage: payload?.hasNextPage ?? false,
        };
      })
      .addCase(getAllMarketUpdates.rejected, (state) => {
        state.isLoading = false;
        state.marketUpdates = [];
      })
      .addCase(createMarketUpdate.pending, (state) => {
        state.isMutating = true;
      })
      .addCase(createMarketUpdate.fulfilled, (state) => {
        state.isMutating = false;
      })
      .addCase(createMarketUpdate.rejected, (state) => {
        state.isMutating = false;
      })
      .addCase(updateMarketUpdate.pending, (state) => {
        state.isMutating = true;
      })
      .addCase(updateMarketUpdate.fulfilled, (state) => {
        state.isMutating = false;
      })
      .addCase(updateMarketUpdate.rejected, (state) => {
        state.isMutating = false;
      })
      .addCase(deleteMarketUpdate.pending, (state) => {
        state.isMutating = true;
      })
      .addCase(deleteMarketUpdate.fulfilled, (state) => {
        state.isMutating = false;
      })
      .addCase(deleteMarketUpdate.rejected, (state) => {
        state.isMutating = false;
      });
  },
});

export const { removeMarketUpdate } = marketUpdateSlice.actions;

export default marketUpdateSlice.reducer;
